import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Phase 5 — useServerTable
 *
 * Hook for register pages that paginate + sort on the server.
 * Replaces `useApi(api.getAll)` for register-style listings.
 *
 * Backend contract (every paged endpoint follows this):
 *   GET /api/<resource>/paged?page=1&size=25&sortBy=createdAt&sortDir=desc&...filters
 *   →  { items: [...], page, size, totalElements, totalPages,
 *        sortBy, sortDir, isFirst, isLast }
 *
 * Usage:
 *   const t = useServerTable(quotationApi.paged, {
 *     defaultSortBy: "createdAt",
 *     defaultSortDir: "desc",
 *     defaultSize: 25,
 *     extraFilters: { customerId, status },   // included in every request
 *   });
 *   <Table
 *     columns={cols}
 *     serverMode
 *     data={t.items}
 *     loading={t.loading}
 *     page={t.page} size={t.size} totalElements={t.totalElements}
 *     sortBy={t.sortBy} sortDir={t.sortDir}
 *     onPageChange={t.setPage} onSizeChange={t.setSize}
 *     onSortChange={t.setSort}
 *   />
 *
 * @param fetchFn   (params) => Promise<{ data: PagedResponse }>
 *                  matches the apiClient pattern: returns axios-like response
 *                  with .data.data.items shape (because of ApiResponse<T> wrapper).
 *                  Hook normalises both .data.items and .data.data.items.
 */
export default function useServerTable(fetchFn, options = {}) {
  const {
    defaultSortBy  = "createdAt",
    defaultSortDir = "desc",
    defaultSize    = 25,
    extraFilters   = {},
    enabled        = true,
    // Phase 36da.99.qspeed — debounce ONLY filter-driven reloads (e.g. search
    // typing) by this many ms.  Page / sort / size changes stay instant.
    // Default 0 = no debounce (unchanged behaviour for other pages).
    debounceMs     = 0,
  } = options;

  const [page,    setPage]    = useState(1);
  const [size,    setSize]    = useState(defaultSize);
  const [sortBy,  setSortBy]  = useState(defaultSortBy);
  const [sortDir, setSortDir] = useState(defaultSortDir);

  const [items,         setItems]         = useState([]);
  const [meta,          setMeta]          = useState({});
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);

  // Stable filter object so consumers can pass inline objects without
  // triggering an infinite reload loop. Stringify is cheap and handles
  // nested values; deep-equal would be overkill.
  const filtersKey = useMemo(() => JSON.stringify(extraFilters || {}), [extraFilters]);

  // Keep latest fetchFn in a ref so changing fetchFn references (rare)
  // don't cause refetch loops. Consumers usually pass the api method
  // directly (stable reference).
  const fetchRef = useRef(fetchFn);
  useEffect(() => { fetchRef.current = fetchFn; }, [fetchFn]);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        page, size, sortBy, sortDir,
        ...JSON.parse(filtersKey),
      };
      // Drop empty / null / undefined filter values so they don't hit the
      // backend as ?customerId=null.
      Object.keys(params).forEach(k => {
        if (params[k] === "" || params[k] === null || params[k] === undefined) {
          delete params[k];
        }
      });
      const res = await fetchRef.current(params);
      // Normalise both wrapped and unwrapped shapes.
      const body = res?.data?.data ?? res?.data ?? {};
      setItems(body.items || []);
      setTotalElements(body.totalElements || 0);
      setTotalPages(body.totalPages || 0);
      setMeta(body);   // expose extra response fields (e.g. grand totals)
      // Backend may correct sortBy/sortDir (e.g., when client passes invalid
      // value and backend falls back to default). Sync local state if so.
      if (body.sortBy && body.sortBy !== sortBy)   setSortBy(body.sortBy);
      if (body.sortDir && body.sortDir !== sortDir) setSortDir(body.sortDir);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to load");
      setItems([]);
      setTotalElements(0);
      setTotalPages(0);
      setMeta({});
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sortBy, sortDir, filtersKey, enabled]);

  // Auto-reload on page / size / sort / enabled changes — always immediate.
  // (Deliberately NOT depending on `reload`/`filtersKey` here so a filter
  // change doesn't double-fire alongside the debounced effect below.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [page, size, sortBy, sortDir, enabled]);

  // Phase 36da.99.qspeed — reload on filter change.  When debounceMs > 0 (e.g.
  // search typing) we wait that long so we don't hit the backend on every
  // keystroke; otherwise it fires immediately (unchanged behaviour).  The
  // initial mount is skipped because the effect above already did the first load.
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) {
      firstFilterRun.current = false;
      return;
    }
    if (!debounceMs) { reload(); return; }
    const handle = setTimeout(() => { reload(); }, debounceMs);
    return () => clearTimeout(handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  // Click on column header: toggles direction if same column, else sets
  // the new column with the default direction. Resets to page 1 since
  // the new ordering invalidates the current page index.
  const setSort = useCallback((newSortBy) => {
    setPage(1);
    setSortBy(prev => {
      if (prev === newSortBy) {
        setSortDir(d => d === "asc" ? "desc" : "asc");
        return prev;
      }
      setSortDir(defaultSortDir);
      return newSortBy;
    });
  }, [defaultSortDir]);

  // When filters change, snap back to page 1 — page N may not exist
  // in the new filtered set.
  useEffect(() => { setPage(1); }, [filtersKey]);

  return {
    // Data
    items, totalElements, totalPages, loading, error, meta,
    // State
    page, size, sortBy, sortDir,
    // Setters (auto-snap page on size/sort changes)
    setPage,
    setSize: (n) => { setSize(n); setPage(1); },
    setSort,
    // Manual refresh (e.g., after create/update/delete mutations)
    reload,
  };
}